import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { COLLECTIONS } from "../data/content";
import { Tag, Reveal } from "../components/Shared";

const Card = ({ c, i, compact = false }) => (
  <Link
    to="/collections"
    data-testid={`collection-card-${c.id}`}
    className={`group relative block shrink-0 overflow-hidden ${compact ? "aspect-[3/4] w-[72vw] snap-center" : "h-[62vh] w-[38vw] min-w-[420px]"}`}
  >
    <img
      src={c.image}
      alt={`${c.label} collection — ${c.caption}`}
      loading="lazy"
      className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
      style={{ transitionProperty: "transform" }}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
    <span className="absolute left-5 top-5 font-mono text-[10px] tracking-[0.3em] text-bone/60">{String(i + 1).padStart(2, "0")}</span>
    <div className="absolute bottom-0 left-0 right-0 p-6">
      <h3 className="font-serif text-3xl font-light tracking-wide transition-colors duration-300 group-hover:text-flame">{c.label}</h3>
      <p className="mt-1 text-xs font-light italic text-bone/60">{c.caption}</p>
      <span className="mt-4 block h-px w-0 bg-flame transition-all duration-700 group-hover:w-full" />
    </div>
  </Link>
);

const Collections = () => {
  const targetRef = useRef(null);
  const trackRef = useRef(null);
  const [range, setRange] = useState(0);
  const { scrollYProgress } = useScroll({ target: targetRef });
  const x = useTransform(scrollYProgress, [0, 1], [0, -range]);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) setRange(Math.max(0, trackRef.current.scrollWidth - window.innerWidth + 80));
    };
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 800);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);

  return (
    <section className="relative bg-ink" data-testid="collections-section">
      <div className="mx-auto max-w-[1600px] px-5 pt-28 sm:px-10 md:pt-40">
        <Reveal><Tag>Collections</Tag></Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-serif text-5xl font-light tracking-tight sm:text-6xl lg:text-7xl">
            STORIES IN <span className="text-fog">SILHOUETTE.</span>
          </h2>
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.35em] text-fog md:block">Scroll — the rack moves with you</p>
        </div>
      </div>

      <div ref={targetRef} className="relative hidden h-[320vh] md:block">
        <div className="sticky top-0 flex h-screen items-center overflow-hidden">
          <motion.div ref={trackRef} style={{ x }} className="flex gap-8 pl-10 will-change-transform">
            {COLLECTIONS.map((c, i) => <Card key={c.id} c={c} i={i} />)}
            <div className="flex h-[62vh] w-[30vw] min-w-[320px] shrink-0 flex-col items-start justify-center">
              <p className="font-serif text-4xl font-light leading-tight">Every story<br />deserves an<br /><span className="text-flame">audience.</span></p>
              <Link to="/collections" data-testid="collections-end-link" className="mt-8 border border-flame px-7 py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink">
                Enter the Archive
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 pb-20 md:hidden" data-testid="collections-mobile-rail">
        {COLLECTIONS.map((c, i) => <Card key={c.id} c={c} i={i} compact />)}
      </div>
    </section>
  );
};

export default Collections;
